'use client'

import { Button } from '@/components/ui/button'
import { useAddToCart } from '@/hooks/react-query/cart/use-cart'
import { VariantMapped } from '@/types/product'
import { Minus, Plus } from 'lucide-react'
import Image from 'next/image'
import { KeyboardEvent, useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

interface ProductInfoSuggestAddCartProps {
  size: string
  color: string
  product: BasicProductInfo
}

interface BasicProductInfo {
  id: string
  display_name: string
  seo_alias: string
  regular_price: number
  compare_price?: number
  percent?: number
  default_thumbnail: string
  addon_note?: string
  available?: boolean
  is_combo?: boolean
  tags?: Record<string, string>
  mapped_variants?: Record<string, VariantMapped[]>
  [key: string]: unknown
}

export const ProductInfoSuggestAddCart = ({
  size,
  color,
  product,
}: ProductInfoSuggestAddCartProps) => {
  const [quantity, setQuantity] = useState(1)
  const { mutateAsync: addToCart } = useAddToCart()

  // Tìm variant dựa trên size và color được chọn
  const selectedVariant = product?.mapped_variants?.[color]?.find(
    (variant) => variant.size?.toLowerCase() === size
  )

  // Reset quantity về 1 khi thay đổi size hoặc color
  useEffect(() => {
    setQuantity(1)
  }, [size, color])

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove any non-digit characters
    const value = e.target.value.replace(/\D/g, '')

    // Prevent leading zeros
    if (value.length > 1 && value[0] === '0') {
      setQuantity(parseInt(value.slice(1)))
      return
    }

    // Don't allow empty value
    if (!value) {
      setQuantity(1)
      return
    }

    // Convert to number and validate range
    const numValue = parseInt(value)
    if (numValue > 999) {
      setQuantity(999)
    } else {
      setQuantity(numValue)
    }
  }

  const handleIncrement = () => {
    if (quantity < 999) {
      setQuantity(quantity + 1)
    }
  }

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      handleIncrement()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      handleDecrement()
    }
  }

  const handleAddToCart = useCallback(async () => {
    if (!selectedVariant) {
      toast.error('Vui lòng chọn kích thước và màu sắc')
      return
    }

    // Kiểm tra xem variant có available hay không
    if (
      selectedVariant.isDisable ||
      (selectedVariant.quantity !== undefined && selectedVariant.quantity <= 0)
    ) {
      toast.error('Sản phẩm đã hết hàng')
      return
    }

    await addToCart({
      payload: {
        product_id: selectedVariant.product_id,
        options: {
          id: selectedVariant.id,
        },
        quantity,
      },
      // fix lại toast
      toastOptions: {
        productName: product.display_name || '',
        variantName: `${selectedVariant?.color || ''} / ${selectedVariant?.size || ''}`,
        regularPrice: product.regular_price,
        comparePrice: product.compare_price || 0,
        imageUrl: selectedVariant.thumbnail || product.default_thumbnail || '',
      },
    })

    //đóng modal
  }, [selectedVariant, quantity, addToCart, product])

  // Kiểm tra nếu variant không có sẵn
  const isDisabled =
    !selectedVariant ||
    selectedVariant.isDisable ||
    (selectedVariant.quantity !== undefined && selectedVariant.quantity <= 0)

  return (
    <div
      className='relative mt-4 h-13 lg:!mt-10'
      data-tracking-level-2={`productdetail_matchinglook_${product?.gender_type}`}
    >
      <div className='absolute top-1/2 z-[1] flex h-full w-fit -translate-y-1/2 items-center rounded-full bg-neutral-600 p-4 text-sm text-light'>
        <button
          className='flex h-6 w-5 items-center justify-center'
          onClick={handleDecrement}
          disabled={isDisabled}
          aria-label='Giảm số lượng'
        >
          <Minus size={12} className='text-light' />
        </button>
        <label htmlFor='quantity-input' className='sr-only'>
          Số lượng
        </label>
        <input
          id='quantity-input'
          type='text'
          className='w-9 border-none bg-inherit text-center text-sm outline-none'
          value={quantity}
          onChange={handleQuantityChange}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          aria-label='Số lượng'
        />
        <button
          className='flex h-6 w-5 items-center justify-center'
          onClick={handleIncrement}
          disabled={isDisabled}
          aria-label='Tăng số lượng'
        >
          <Plus size={12} className='text-light' />
        </button>
      </div>
      <Button
        className='h-full w-full gap-2'
        variant={'black'}
        onClick={handleAddToCart}
        disabled={isDisabled}
        data-tracking-level-3={'AddCart'}
      >
        <Image
          className='h-4 w-4 max-lg:hidden'
          src='/icons/product/icon-cart.svg'
          width={16}
          height={16}
          alt='add cart icon'
        />
        <p className='font-criteria text-base font-medium normal-case leading-6 max-lg:pl-10'>
          {isDisabled ? 'Hết hàng' : 'Thêm vào giỏ'}
        </p>
      </Button>
    </div>
  )
}
